import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, ArrowRight, Inbox, CheckCheck, Loader2, Send, X, ExternalLink } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * ProspectAlertsCard — Command Centre lead-pipe indicator for VCs who
 * submitted the public Express Interest form on /investors. Polls every
 * 60s and shows a coral badge whenever unread submissions exist.
 *
 * Backend surfaces:
 *   GET  /api/vault/prospect-alerts/count-public   (public count for the badge)
 *   GET  /api/vault/prospect-alerts                (vault auth, full list)
 *   POST /api/vault/prospect-alerts/ack-all
 *
 * Designed to gracefully degrade: if the vault session is expired we still
 * show the unread COUNT but route the "View" link to /admin/vault for the
 * founder to re-authenticate.
 */

const PALETTE = {
  bg: '#FFFFFF',
  border: 'rgba(92,82,106,0.10)',
  coral: '#F99B7D',
  mint: '#A3E2C9',
  text: '#2A2235',
  muted: '#5C526A',
};

const fmtTs = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000); // minutes
    if (diff < 1) return 'just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
};

const ProspectAlertsCard = () => {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acking, setAcking] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // active alert being replied to

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // 1) lightweight unauthenticated count first (always works)
      const c = await apiClient.request('/api/vault/prospect-alerts/count-public').catch(() => null);
      if (c?.unread_count !== undefined) setCount(c.unread_count);

      // 2) full authenticated list — only attempt when the vault session is
      // actually open (session-check never 401s), so the console stays clean.
      const sess = await apiClient.request('/api/vault/session-check').catch(() => null);
      const full = sess?.active
        ? await apiClient.request('/api/vault/prospect-alerts').catch(() => null)
        : null;
      if (full?.unread !== undefined) {
        setAuthed(true);
        setCount(full.unread_count || 0);
        setItems(full.unread || []);
      } else {
        setAuthed(false);
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const i = setInterval(load, 60000); return () => clearInterval(i); }, [load]);

  const onAckAll = async () => {
    if (!authed || count === 0) return;
    setAcking(true);
    try {
      await apiClient.request('/api/vault/prospect-alerts/ack-all', { method: 'POST' });
      await load();
    } finally {
      setAcking(false);
    }
  };

  return (
    <>
    <div
      className="rounded-2xl p-5 transition-shadow"
      style={{ background: PALETTE.bg, border: `1px solid ${PALETTE.border}`, borderLeft: `4px solid ${count > 0 ? PALETTE.coral : PALETTE.mint}` }}
      data-testid="prospect-alerts-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: count > 0 ? PALETTE.coral + '22' : PALETTE.mint + '33' }}>
            <Bell size={16} style={{ color: count > 0 ? '#9A4A33' : '#1E6F4F' }} />
          </div>
          <div>
            <h3 className="text-sm font-bold leading-tight" style={{ color: PALETTE.text }}>
              Investor Gateway — Express Interest
            </h3>
            <p className="text-xs" style={{ color: PALETTE.muted }}>
              Prospect VCs hovering at the public gate
            </p>
          </div>
        </div>
        <span
          className="px-2.5 py-1 rounded-full text-xs font-black"
          style={{
            background: count > 0 ? PALETTE.coral : '#EEF2EE',
            color: count > 0 ? '#FFFFFF' : PALETTE.muted,
            minWidth: 28,
            textAlign: 'center',
          }}
          data-testid="prospect-alerts-count"
        >
          {loading ? <Loader2 size={12} className="animate-spin inline" /> : count}
        </span>
      </div>

      {/* Body */}
      {count === 0 ? (
        <div className="text-xs flex items-center gap-2 py-2" style={{ color: PALETTE.muted }} data-testid="prospect-alerts-empty">
          <Inbox size={12} /> No new prospect submissions. The /investors gateway is quiet.
        </div>
      ) : authed ? (
        <ul className="space-y-2 mb-3" data-testid="prospect-alerts-list">
          {items.slice(0, 4).map((a) => (
            <li
              key={a.alert_id}
              className="flex items-start justify-between gap-3 px-3 py-2 rounded-lg transition-opacity"
              style={{
                background: a.replied ? '#F1F4F1' : '#FAFAED',
                opacity: a.replied ? 0.55 : 1,
              }}
              data-testid={`prospect-alert-${a.alert_id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate flex items-center gap-2" style={{ color: PALETTE.text }}>
                  <span className="truncate">{a.name}</span>
                  {a.replied && (
                    <span
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap"
                      style={{ background: PALETTE.mint, color: '#1E6F4F' }}
                      data-testid={`prospect-alert-replied-${a.alert_id}`}
                    >
                      <CheckCheck size={9} /> Replied {fmtTs(a.replied_at)}
                    </span>
                  )}
                </div>
                <div className="text-xs truncate" style={{ color: PALETTE.muted }}>
                  {a.email}{a.firm ? ` · ${a.firm}` : ''}{a.ticket_size ? ` · ${a.ticket_size}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {!a.replied && (
                  <button
                    type="button"
                    onClick={() => setReplyTo(a)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold transition-transform hover:scale-[1.03]"
                    style={{ background: PALETTE.coral, color: '#fff' }}
                    data-testid={`prospect-alert-reply-${a.alert_id}`}
                  >
                    <Send size={10} /> Reply
                  </button>
                )}
                <span className="text-[10px] whitespace-nowrap" style={{ color: PALETTE.muted }}>{fmtTs(a.created_at)}</span>
              </div>
            </li>
          ))}
          {items.length > 4 && (
            <li className="text-xs px-3" style={{ color: PALETTE.muted }}>
              + {items.length - 4} more unread
            </li>
          )}
        </ul>
      ) : (
        <p className="text-xs mb-3 px-3 py-2 rounded-lg" style={{ background: '#FAFAED', color: PALETTE.muted }} data-testid="prospect-alerts-locked">
          {count} unread submission{count === 1 ? '' : 's'} waiting. Unlock the Secure Vault to review.
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between gap-3 pt-2 border-t" style={{ borderColor: PALETTE.border }}>
        <Link
          to="/admin/vault?tab=investors"
          className="text-xs font-bold inline-flex items-center gap-1.5 hover:opacity-70 transition-opacity"
          style={{ color: PALETTE.coral }}
          data-testid="prospect-alerts-view-link"
        >
          Open in Secure Vault <ArrowRight size={11} />
        </Link>
        {authed && count > 0 && (
          <button
            onClick={onAckAll}
            disabled={acking}
            className="text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors disabled:opacity-60"
            style={{ background: '#FAFAED', color: PALETTE.text, border: `1px solid ${PALETTE.border}` }}
            data-testid="prospect-alerts-ack-all-btn"
          >
            {acking ? <Loader2 size={11} className="animate-spin" /> : <CheckCheck size={11} />}
            Mark all read
          </button>
        )}
      </div>
    </div>
    {replyTo && (
      <QuickReplyModal
        alert={replyTo}
        onClose={() => setReplyTo(null)}
        onSent={() => { setReplyTo(null); load(); }}
      />
    )}
    </>
  );
};


// ── Quick Reply composer ──────────────────────────────────────────────────
const QUICK_REPLY_TEMPLATES = [
  {
    id: 'dr-invite',
    label: 'Send DR invite now',
    subject: (a) => `HiBFF — Data-room invitation${a.firm ? ` for ${a.firm}` : ''}`,
    body: (a) => [
      `Hi ${a.name?.split(' ')[0] || 'there'},`,
      '',
      `Thanks for the interest from ${a.firm || 'your end'}${a.ticket_size ? ` — noted on the ${a.ticket_size} indication` : ''}.`,
      '',
      "I've issued a fresh 6-digit passcode to your secure HiBFF data room. It includes:",
      '• Founder dossier · live cap-table · dynamic valuation engine',
      '• Schools programme + signed letters of intent',
      '• Growth simulator · cost-to-replicate breakdown',
      '',
      "Passcode and link arrive in a follow-up email within 5 minutes. Let me know if you'd like a 20-min walkthrough on Zoom either side of it.",
      '',
      'Warmly,',
      'Robert Parson',
      'Founder, HiBFF',
    ].join('\n'),
  },
  {
    id: 'intro-call',
    label: 'Schedule 20-min intro',
    subject: (a) => `Re: HiBFF — quick intro call${a.firm ? `, ${a.firm}` : ''}`,
    body: (a) => [
      `Hi ${a.name?.split(' ')[0] || 'there'},`,
      '',
      `Really appreciate you reaching out${a.firm ? ` from ${a.firm}` : ''}.`,
      '',
      "Let's keep this efficient — 20 minutes over Zoom and you'll have a clear picture of where we are, the unit economics, and what makes the moment timely. Three slots that work for me this week:",
      '',
      '  · Tue 15:00 GMT',
      '  · Wed 11:00 GMT',
      '  · Thu 16:30 GMT',
      '',
      'Reply with whichever lands best and I will send a calendar invite + the Zoom link.',
      '',
      'Warmly,',
      'Robert Parson',
      'Founder, HiBFF',
    ].join('\n'),
  },
  {
    id: 'polite-decline',
    label: 'Polite decline / not a fit',
    subject: (a) => `Re: HiBFF — thank you for the consideration${a.firm ? `, ${a.firm}` : ''}`,
    body: (a) => [
      `Hi ${a.name?.split(' ')[0] || 'there'},`,
      '',
      `Sincerely appreciate the note${a.firm ? ` from ${a.firm}` : ''} and the time you took to look into HiBFF.`,
      '',
      "Stage-and-shape wise we're not aligned right now, but I'd love to keep the door open for the next phase. I'll add you to my Monday Investor Pulse so you can watch the metrics build — and if anything shifts on either side, please reach out.",
      '',
      'Warmly,',
      'Robert Parson',
      'Founder, HiBFF',
    ].join('\n'),
  },
];

const QuickReplyModal = ({ alert, onClose, onSent }) => {
  const defaultSubject = `Re: HiBFF — thanks for your enquiry${alert.firm ? `, ${alert.firm}` : ''}`;
  const defaultBody = [
    `Hi ${alert.name?.split(' ')[0] || 'there'},`,
    '',
    `Thank you for reaching out about HiBFF${alert.firm ? ` from ${alert.firm}` : ''}.${alert.ticket_size ? ` Noted your indicative ticket of ${alert.ticket_size}.` : ''} I'd love to share more about where we are and what makes the moment timely.`,
    '',
    "Would 20 minutes this week work? I can also send a tailored data-room invitation in the meantime — just let me know which is more useful.",
    '',
    'Warmly,',
    'Robert Parson',
    'Founder, HiBFF',
  ].join('\n');

  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [activeTemplate, setActiveTemplate] = useState('');

  const applyTemplate = (tplId) => {
    if (!tplId) {
      setSubject(defaultSubject);
      setBody(defaultBody);
      setActiveTemplate('');
      return;
    }
    const tpl = QUICK_REPLY_TEMPLATES.find((t) => t.id === tplId);
    if (!tpl) return;
    setSubject(tpl.subject(alert));
    setBody(tpl.body(alert));
    setActiveTemplate(tplId);
  };

  const onSend = async () => {
    setError('');
    if (!subject.trim() || !body.trim()) {
      setError('Subject and body are required.');
      return;
    }
    setSending(true);
    try {
      await apiClient.request('/api/vault/prospect-alerts/quick-reply', {
        method: 'POST',
        body: JSON.stringify({
          to_email: alert.email,
          subject: subject.trim(),
          body: body,
          alert_id: alert.alert_id,
        }),
      });
      onSent();
    } catch (e) {
      setError(e?.message || 'Email gateway unavailable. Try "Open in mail app" instead.');
    } finally {
      setSending(false);
    }
  };

  const mailtoHref = `mailto:${encodeURIComponent(alert.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(42,34,53,0.55)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="quick-reply-modal"
    >
      <div
        className="rounded-3xl max-w-lg w-full p-6"
        style={{ background: '#FFFFFF', border: '1px solid rgba(92,82,106,0.10)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] mb-1" style={{ color: PALETTE.coral }}>Quick Reply</div>
            <h3 className="text-lg font-black" style={{ color: PALETTE.text }}>{alert.name}</h3>
            <p className="text-xs" style={{ color: PALETTE.muted }}>
              {alert.email}{alert.firm ? ` · ${alert.firm}` : ''}{alert.ticket_size ? ` · ${alert.ticket_size}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid="quick-reply-close">
            <X size={16} style={{ color: PALETTE.muted }} />
          </button>
        </div>

        {alert.message && (
          <div className="text-xs px-3 py-2 rounded-lg mb-3" style={{ background: '#FAFAED', color: PALETTE.muted }}>
            <strong style={{ color: PALETTE.text }}>Their note:</strong> {alert.message}
          </div>
        )}

        {/* Templates dropdown — instant tone-setting */}
        <div className="mb-3" data-testid="quick-reply-templates">
          <label className="block text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: PALETTE.muted }}>Template</label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => applyTemplate('')}
              className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
              style={{
                background: activeTemplate === '' ? PALETTE.coral : '#FAFAED',
                color: activeTemplate === '' ? '#fff' : PALETTE.text,
                border: `1px solid ${activeTemplate === '' ? PALETTE.coral : PALETTE.border}`,
              }}
              data-testid="quick-reply-template-default"
            >
              Bespoke (default)
            </button>
            {QUICK_REPLY_TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => applyTemplate(t.id)}
                className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
                style={{
                  background: activeTemplate === t.id ? PALETTE.coral : '#FAFAED',
                  color: activeTemplate === t.id ? '#fff' : PALETTE.text,
                  border: `1px solid ${activeTemplate === t.id ? PALETTE.coral : PALETTE.border}`,
                }}
                data-testid={`quick-reply-template-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <label className="block mb-3">
          <span className="block text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: PALETTE.muted }}>Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded-xl px-4 py-2 text-sm"
            style={{ background: '#FAFAED', border: `1px solid ${PALETTE.border}`, color: PALETTE.text }}
            data-testid="quick-reply-subject"
          />
        </label>

        <label className="block mb-3">
          <span className="block text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: PALETTE.muted }}>Message</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={9}
            className="w-full rounded-xl px-4 py-2 text-sm resize-y"
            style={{ background: '#FAFAED', border: `1px solid ${PALETTE.border}`, color: PALETTE.text, lineHeight: 1.5 }}
            data-testid="quick-reply-body"
          />
        </label>

        {error && (
          <div className="text-xs px-3 py-2 rounded-lg mb-3" style={{ background: PALETTE.coral + '22', color: '#9A4A33' }} data-testid="quick-reply-error">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <a
            href={mailtoHref}
            className="text-xs font-semibold inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity"
            style={{ color: PALETTE.muted }}
            data-testid="quick-reply-mailto"
          >
            <ExternalLink size={11} /> Open in mail app
          </a>
          <button
            onClick={onSend}
            disabled={sending}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-transform disabled:opacity-60"
            style={{ background: PALETTE.coral, color: '#fff' }}
            data-testid="quick-reply-send-btn"
          >
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            {sending ? 'Sending…' : 'Send via Postmark'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProspectAlertsCard;
